import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ActionIcon,
  Center,
  Group,
  MultiSelect,
  Pagination,
  Skeleton,
  Table,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { ErrorAlert, Loading } from "@sweco-se/reality-frontend";
import { keepPreviousData } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  flexRender,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { RegisteredRouter, RouteIds } from "@tanstack/router-core";
import { createColumnHelper, getCoreRowModel } from "@tanstack/table-core";
import { useMemo } from "react";
import z from "zod";

import { api } from "@/api.ts";
import Area from "@/components/Area.tsx";
import { DatasetGroupIcons } from "@/components/DatasetGroupIcons.tsx";
import { ProjectSelect } from "@/components/ProjectSelect.tsx";
import { TransactionAmount } from "@/components/TransactionAmount.tsx";
import { useCatalogItems } from "@/hooks/useCatalogItems.ts";
import { useSuspenseQueryPostgrest } from "@/hooks/useSuspenseQueryPostgrest.ts";

export const transactionsQuerySchema = z.object({
  project: z.coerce.number().optional(),
  service: z
    .array(z.union([z.enum(["permission_add", "download"]), z.string()]))
    .optional(),
  performedBy: z.string().optional(),
  performedAtFrom: z.iso.date().optional(),
  performedAtTo: z.iso.date().optional(),
  pageIndex: z.coerce.number().min(0).optional(),
  pageSize: z.coerce.number().min(5).max(500).optional(),
});
export type TransactionsQuery = z.infer<typeof transactionsQuerySchema>;

function cleanQuery(query: TransactionsQuery): TransactionsQuery {
  const q = { ...query };
  if (q.pageIndex === 0) {
    delete q.pageIndex;
  }
  if (q.pageSize === 50) {
    delete q.pageSize;
  }
  if (q.service && q.service.length === 0) {
    delete q.service;
  }
  if (q.performedBy !== undefined && q.performedBy.length === 0) {
    delete q.performedBy;
  }
  return q;
}

export const TransactionsTable = <
  T extends RouteIds<RegisteredRouter["routeTree"]>,
>({
  fixedValues,
  route,
}: {
  fixedValues?: Partial<TransactionsQuery>;
  route: T;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
  const search = useSearch({
    from: route,
    structuralSharing: false,
  }) as TransactionsQuery;
  const query = useMemo(
    () => ({ pageIndex: 0, pageSize: 50, ...search, ...fixedValues }),
    [search, fixedValues],
  );
  const navigate = useNavigate();
  const setQuery = (q: TransactionsQuery) => {
    navigate({
      to: ".",
      search: (prev) => cleanQuery({ ...prev, ...q }) as any,
    });
  };

  const { data, count, error, isLoading } = useQuery(
    useMemo(() => {
      let q = api
        .from("transaction")
        .select(
          "*,dataset(uri),service_profile(name),source_information(reader),session(id,started_by,started_at,product(name,type),top_level_objects,project_id,price)",
          {
            count: "estimated",
          },
        )
        .range(
          query.pageIndex * query.pageSize,
          (query.pageIndex + 1) * query.pageSize - 1,
        )
        .order("performed_at", { ascending: false })
        .order("dataset_id");

      if (query.project) {
        q = q.eq("project_id", query.project);
      }
      if (query.service && query.service.length > 0) {
        q = q.or(
          query.service
            .map((s) =>
              ["permission_add", "download"].includes(s)
                ? `type.eq.${s}`
                : `service_profile.eq.${s}`,
            )
            .join(","),
        );
      }
      if (query.performedBy) {
        q = q.ilike("performed_by", `${query.performedBy}%`);
      }
      if (query.performedAtFrom && query.performedAtTo) {
        q = q.or(
          `and(performed_at.gte.${query.performedAtFrom},performed_at.lte.${query.performedAtTo})`,
        );
      } else if (query.performedAtFrom) {
        q = q.gte("performed_at", query.performedAtFrom);
      } else if (query.performedAtTo) {
        q = q.lte("performed_at", query.performedAtTo);
      }

      return q;
    }, [query]),
    {
      placeholderData: keepPreviousData,
    },
  );
  const catalog = useCatalogItems();

  const { t, i18n } = useLingui();

  const { data: projects } = useSuspenseQueryPostgrest(
    api.from("project").select("id,identifier,name"),
  );
  const rows = useMemo(() => {
    const sessionRows = (data ?? [])
      .map((r) => r.session)
      .filter((s) => s !== null)
      .filter((s, idx, lst) => lst.findIndex((s2) => s2.id === s.id) === idx)
      .map((s) => ({
        // remap keys to match transaction keys, so that the table on has to handle one set of keys
        id: s.id,
        project_id: s.project_id,
        direction: "session",
        cost: s.price,
        type:
          s.product.length > 0 &&
          s.product.every((p) => p.type === s.product[0].type)
            ? s.product[0].type
            : "session",
        performed_at: s.started_at,
        performed_by: s.started_by,
        amount: Object.entries(s.top_level_objects ?? {})
          .map(([_k, v]) => v.length)
          .reduce((a, b) => a + b, 0),
        subresource: s.product.map((p) => p.name),
        dataset:
          Object.keys(s.top_level_objects ?? {}).length === 1
            ? { uri: Object.keys(s.top_level_objects ?? {})[0] }
            : null,
        source_information: null,
        service_profile: null,
        request_type: null,
      }));
    const sessionlessTransactionRows = (data ?? []).filter(
      (r) => r.session === null,
    );
    const combinedRows = [...sessionRows, ...sessionlessTransactionRows];
    // sort by performed_at descending
    combinedRows.sort(
      (a, b) =>
        new Date(b.performed_at).getTime() - new Date(a.performed_at).getTime(),
    );
    return combinedRows;
  }, [data]);
  const columns = useMemo(() => {
    const helper = createColumnHelper<(typeof rows)[number]>();
    return [
      ...("project" in (fixedValues ?? [])
        ? []
        : [
            helper.accessor("project_id", {
              header: t`Project`,
              cell: (info) => {
                const project = projects.find((p) => p.id === info.getValue());
                return project ? (
                  <>
                    {project.identifier} ({project.name})
                  </>
                ) : (
                  t`Unknown`
                );
              },
            }),
          ]),
      helper.accessor("dataset", {
        header: t`Dataset`,
        cell: (info) => {
          const dataset = info.getValue();
          if (!dataset) {
            return t`Unknown`;
          }
          return catalog.forUri(dataset.uri)?.properties.title || t`Unknown`;
        },
      }),
      helper.accessor("type", {
        header: t`Service`,
        cell: (info) =>
          info.getValue() === "property-search" ? (
            t`Property Search`
          ) : info.getValue() === "permission_add" ? (
            t`Permission request`
          ) : info.getValue() === "download" ? (
            t({ message: "Download", context: "noun" })
          ) : (
            <Tooltip
              label={
                <Trans>
                  Reader:{" "}
                  {info.row.original.source_information?.reader ?? t`Unknown`}
                </Trans>
              }
            >
              <span>
                {info.row.original.service_profile?.name ===
                "https://stacspec.org"
                  ? "STAC"
                  : info.row.original.service_profile?.name ===
                      "http://www.opengis.net/def/interface/ogcapi-features"
                    ? "OGC API - Features"
                    : info.row.original.service_profile?.name ===
                        "https://api.lantmateriet.se/distribution/produkter"
                      ? t({ message: "Direct API", context: "Lantmäteriet" })
                      : t`Unknown`}
              </span>
            </Tooltip>
          ),
      }),
      helper.accessor("subresource", {
        header: t`Subresource`,
        cell: (info) =>
          info.row.original.direction === "session" ? (
            <DatasetGroupIcons include={info.getValue() ?? []} />
          ) : info.row.original.request_type?.startsWith("find:") ? (
            <Tooltip label={info.row.original.request_type.substring(5)}>
              <span>
                <Trans>Find references</Trans>
              </span>
            </Tooltip>
          ) : (
            (info.getValue()?.join(", ") ?? "")
          ),
      }),
      helper.accessor("performed_by", {
        header: t`Performed by`,
        cell: (info) => info.getValue(),
      }),
      helper.accessor("performed_at", {
        header: t`Performed at`,
        cell: (info) => i18n.date(info.getValue()),
      }),
      helper.accessor("amount", {
        header: t`Amount`,
        cell: (info) => {
          const transaction = info.row.original;
          return (
            <>
              {transaction.direction === "session" &&
              transaction.type === "property-search" ? (
                <Trans>{i18n.number(transaction.amount)} objects</Trans>
              ) : transaction.type === "service_access" ? (
                <TransactionAmount
                  value={transaction.amount}
                  profile={transaction.service_profile?.name}
                />
              ) : transaction.type === "download" ? (
                <Area value={transaction.amount} />
              ) : (
                ""
              )}
              {/* hack that ensures that all rows have the same height */}
              <sup style={{ visibility: "hidden" }}>&nbsp;</sup>
            </>
          );
        },
      }),
      helper.accessor("cost", {
        header: t`Cost`,
        cell: (info) =>
          info.getValue()
            ? i18n.number(info.getValue(), {
                style: "currency",
                currency: "SEK",
                currencyDisplay: "code",
                maximumFractionDigits: 2,
              })
            : "-",
      }),
      helper.accessor(() => "No", {
        id: "transferred",
        header: t`Transferred to TEM`,
        cell: () => t`No`,
      }),
    ];
  }, [catalog, fixedValues, i18n, t, projects]);

  const table = useReactTable({
    data: rows,
    getSubRows: (row) =>
      row.direction === "session"
        ? (data?.filter((r) => r.session?.id === row.id) ?? [])
        : [],
    columns,
    state: {
      pagination: { pageIndex: query.pageIndex, pageSize: query.pageSize },
    },
    onPaginationChange: (pagination) => {
      setQuery(
        typeof pagination === "function"
          ? pagination({
              pageIndex: query.pageIndex,
              pageSize: query.pageSize,
            })
          : pagination,
      );
    },
    pageCount: Math.ceil((count ?? 0) / query.pageSize),
    manualFiltering: true,
    manualSorting: true,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  return (
    <>
      <Group>
        {"project" in (fixedValues ?? {}) || (
          <ProjectSelect
            placeholder={t`Filter by projects`}
            size="xs"
            value={query.project}
            onChange={(value) =>
              setQuery({ project: value ?? undefined, pageIndex: 0 })
            }
          />
        )}
        {"service" in (fixedValues ?? {}) || (
          <MultiSelect
            placeholder={t`Filter by services`}
            size="xs"
            clearable
            value={query.service ?? []}
            onChange={(value) => setQuery({ service: value, pageIndex: 0 })}
            data={[
              { value: "permission_add", label: t`Permission request` },
              {
                value: "download",
                label: t({ message: "Download", context: "noun" }),
              },
              ...catalog.data.features
                .flatMap((f) => f.links)
                .filter((l) => l.rel === "service" && l.profile && l.title)
                .map((l) => ({
                  value: l.profile![0],
                  label: l.title!,
                }))
                .filter(
                  (v, i, a) =>
                    a.findIndex((val) => val.value === v.value) === i,
                ),
            ]}
          />
        )}
        {"performedBy" in (fixedValues ?? {}) || (
          <TextInput
            placeholder={t`Filter by users`}
            size="xs"
            value={query.performedBy ?? ""}
            onChange={(evt) =>
              setQuery({ performedBy: evt.target.value, pageIndex: 0 })
            }
          />
        )}
        {"performedAtFrom" in (fixedValues ?? {}) ||
          "performedAtTo" in (fixedValues ?? {}) || (
            <DatePickerInput
              placeholder={t`Filter by date`}
              size="xs"
              type="range"
              withWeekNumbers
              clearable
              style={{ flexGrow: 1 }}
              value={[
                query.performedAtFrom ?? null,
                query.performedAtTo ?? null,
              ]}
              onChange={(value) =>
                setQuery({
                  performedAtFrom: value[0]
                    ? new Date(value[0]).toISOString().substring(0, 10)
                    : undefined,
                  performedAtTo: value[1]
                    ? new Date(value[1]).toISOString().substring(0, 10)
                    : undefined,
                  pageIndex: 0,
                })
              }
            />
          )}
        <Loading hidden={!isLoading} />
      </Group>
      <Table my="sm">
        <Table.Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.Th
                  key={header.id}
                  colSpan={header.colSpan}
                  style={{ textWrap: "nowrap" }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </Table.Th>
              ))}
            </Table.Tr>
          ))}
        </Table.Thead>
        <Table.Tbody>
          {error ? (
            <Table.Tr>
              <Table.Td colSpan={columns.length}>
                <ErrorAlert>{error.message}</ErrorAlert>
              </Table.Td>
            </Table.Tr>
          ) : null}
          {count === null ? (
            <Table.Tr>
              {table.getHeaderGroups()[0].headers.map((header) => (
                <Table.Td key={header.id} colSpan={header.colSpan}>
                  <Skeleton height={12} />
                </Table.Td>
              ))}
            </Table.Tr>
          ) : count === 0 ? (
            <Table.Tr>
              <Table.Td
                colSpan={8}
                align="center"
                style={{ paddingTop: 20, paddingBottom: 20 }}
              >
                <Trans>No transactions found</Trans>
              </Table.Td>
            </Table.Tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <Table.Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Table.Td
                    key={cell.id}
                    style={{
                      paddingLeft:
                        cell.column.id === "dataset"
                          ? `${row.depth * 2}rem`
                          : undefined,
                    }}
                  >
                    {cell.column.id === "dataset" ? (
                      <ActionIcon
                        onClick={row.getToggleExpandedHandler()}
                        variant="transparent"
                        size="xs"
                        mr="sm"
                        style={{
                          visibility: row.getCanExpand() ? undefined : "hidden",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          rotation={row.getIsExpanded() ? undefined : 270}
                        />
                      </ActionIcon>
                    ) : null}
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </Table>
      <Center>
        <Pagination
          total={table.getPageCount()}
          value={table.getState().pagination.pageIndex + 1}
          onChange={(page) => table.setPageIndex(page - 1)}
        />
      </Center>
    </>
  );
};
